
import {
	memo, useCallback, useEffect,
	useRef
} from 'react'

import { ApiCaller } from '@frontend/api'
import { type Content, isValidContentType, type VectorType } from '@types'
import type { ReactPdfViewerParams } from '@ui/chat/modules/ReactPdfViewer'
import { LoadingSpinner } from '@ui/components'

import { useTooltip } from '../../SourceTooltipProvider'

// Type definitions
interface TooltipProps {
	readonly isLoading: boolean;
	readonly excerpt: string;
	readonly index: string;
	readonly contents?: Content[] | undefined;
	readonly contentId?: string | undefined;
	readonly messageId?: string | undefined;
	readonly imageUrl?: string | undefined;
	readonly vectorId?: string | undefined;
	readonly type?: VectorType | undefined;
	readonly page: number;
	readonly onSourceSelected?: ((params: ReactPdfViewerParams) => void) | undefined;
}

const VISIONEUSE_ACCEPTED_CONTENT_TYPES = [
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.ms-powerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation'
]

function InlineSource({ isLoading, contents, contentId, vectorId, messageId, excerpt, index, imageUrl, page, type, onSourceSelected }: TooltipProps) {
	const content = contents?.find(content => content.id === contentId)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const { openTooltip, closeTooltip } = useTooltip()

	useEffect(() => {
		function handleScroll() {
			if (triggerRef.current) {
				closeTooltip()
			}
		}

		if (triggerRef.current) {
			triggerRef.current.addEventListener('scroll', handleScroll)
		}

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [triggerRef])

	async function handleCheckVisualisator() {
		const fileTypeAccepted = content?.type && VISIONEUSE_ACCEPTED_CONTENT_TYPES.includes(content.type) && isValidContentType(content.type)

		if ((fileTypeAccepted && content) || messageId) {
			onSourceSelected?.({
				sourceFile: {
					fromUpload: content?.source === 'upload',
					sourceFile: content?.url ?? '',
					excerpt,
					filename: content?.title ?? '',
					contentId: content?.id ?? '',
					messageId,
					source: content?.source ?? '',
					type: type ?? 'text',
					isExcel: content?.type === 'application/vnd.ms-excel' || content?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
					vectorId,
					page,
					isImage: Boolean(imageUrl)
				}
			})

			handleMouseLeave()

			return
		}

		window.open(contentId === 'perplexity' ? excerpt : content?.url, '_blank')
	}

	const handleMouseEnter = useCallback(() => {
		if (triggerRef.current) {
			const rect = triggerRef.current.getBoundingClientRect()

			openTooltip({
				contentId,
				messageId,
				excerpt,
				content,
				index,
				imageUrl,
				position: {
					rect
				}
			})
		}
	}, [content, contentId, excerpt, index, openTooltip])

	const handleMouseLeave = useCallback(() => {
		closeTooltip()
	}, [closeTooltip])

	if (isLoading) {
		return (
			<span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-100 text-blue-700 text-[10px] font-medium transition-colors relative -top-[1.5px]">
				<LoadingSpinner style="gray" size="xsmall" />
			</span>
		)
	}

	if (!content && !messageId && contentId !== 'perplexity') {
		return null
	}

	const colorStyle = {
		text: 'bg-blue-100 text-blue-800',
		summary: 'bg-blue-100 text-blue-800',
		title: 'bg-blue-100 text-blue-800',
		table: 'bg-blue-100 text-blue-800',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		timed_text: 'bg-blue-100 text-blue-800',
		audio: 'bg-blue-100 text-blue-800',
		image: 'bg-blue-100 text-blue-800'
	}[type || 'text']

	return (
		<span className="relative inline-block">
			<button
				ref={ triggerRef }
				className={ `mx-[2px] ${colorStyle} rounded-full w-4 h-4 flex items-center justify-center text-sm font-bold shadow-sm transition-all duration-200` }
				onMouseEnter={ handleMouseEnter }
				onMouseLeave={ handleMouseLeave }
				onClick={ () => {
					ApiCaller.track('Inline_source_opened')
					handleCheckVisualisator()
				} }
			>
				{ index }
			</button>
		</span>
	)
}

// Wrap with memo for performance
export default memo(InlineSource)

